import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Users, Shield, User, Check, X, Clock } from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const PAGE_OPTIONS = [
  { key: 'dashboard', label: 'Gösterge Paneli' },
  { key: 'pos', label: 'POS Satış' },
  { key: 'satis_rapor', label: 'Satış Raporu' },
  { key: 'urunler', label: 'Ürünler' },
  { key: 'stok', label: 'Stok Giriş/Çıkış' },
  { key: 'fiyat_gor', label: 'Fiyat Gör' },
  { key: 'teklif', label: 'Teklif Formu' },
  { key: 'hesaplama', label: 'Hesaplama Aracı' },
  { key: 'musteriler', label: 'Müşteriler / Veresiye' },
  { key: 'gelir_gider', label: 'Gelir / Gider' },
  { key: 'apartman', label: 'Apartman Yönetimi' },
  { key: 'yemek_siparis', label: 'Yemek Sipariş' },
  { key: 'yemek_mutfak', label: 'Mutfak / Menü' },
  { key: 'is_takibi', label: 'İş Takibi' },
  { key: 'teknik_servis', label: 'Teknik Servis' },
  { key: 'vitrin', label: 'Ürün Vitrini (Sıfır / 2. El)' },
  { key: 'bulut', label: 'Bulut Depolama' },
  { key: 'solar', label: 'Solar System' },
  { key: 'akilli', label: 'Akıllı Takip' },
];

export default function HesapUsersPage() {
  const { token, user: currentUser } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', phone: '', role: 'user', permissions: [] });
  const [tab, setTab] = useState('approved');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, { headers });
      if (res.ok) setUsers(await res.json());
    } finally { setLoading(false); }
  };

  const pendingUsers = users.filter(u => u.status === 'pending');
  const approvedUsers = users.filter(u => u.status !== 'pending' && u.status !== 'rejected');
  const rejectedUsers = users.filter(u => u.status === 'rejected');

  const openDialog = (user = null) => {
    if (user) {
      setEditing(user);
      setForm({
        email: user.email, password: '', full_name: user.full_name,
        phone: user.phone || '', role: user.role,
        permissions: user.permissions || []
      });
    } else {
      setEditing(null);
      setForm({ email: '', password: '', full_name: '', phone: '', role: 'user', permissions: ['dashboard'] });
    }
    setShowDialog(true);
  };

  const togglePermission = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const saveUser = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) { toast.error('Ad ve email gerekli'); return; }
    if (!editing && !form.password) { toast.error('Şifre gerekli'); return; }
    setSaving(true);
    try {
      const body = { ...form, status: 'approved' };
      if (editing && !body.password) delete body.password;
      const url = editing ? `${API}/admin/users/${editing.id}` : `${API}/admin/users`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Hata');
      }
      toast.success(editing ? 'Kullanıcı guncellendi' : 'Kullanıcı olusturuldu');
      setShowDialog(false);
      loadUsers();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const approveUser = async (u) => {
    // Onayla ve izinleri secmek icin dialog ac
    openDialog(u);
  };

  const rejectUser = async (u) => {
    if (!window.confirm(`"${u.full_name}" reddedilsin mi?`)) return;
    await fetch(`${API}/admin/users/${u.id}`, {
      method: 'PUT', headers, body: JSON.stringify({ status: 'rejected' })
    });
    toast.success('Kullanıcı reddedildi');
    loadUsers();
  };

  const deleteUser = async (u) => {
    if (u.id === currentUser?.id) { toast.error('Kendinizi silemezsiniz'); return; }
    if (!window.confirm(`"${u.full_name}" silinsin mi?`)) return;
    await fetch(`${API}/admin/users/${u.id}`, { method: 'DELETE', headers });
    toast.success('Kullanıcı silindi');
    loadUsers();
  };

  const toggleActive = async (u) => {
    await fetch(`${API}/admin/users/${u.id}`, {
      method: 'PUT', headers, body: JSON.stringify({ is_active: !u.is_active })
    });
    toast.success(u.is_active ? 'Devre disi birakildi' : 'Aktif edildi');
    loadUsers();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const UserTable = ({ userList, showActions = true }) => (
    <div className="bg-white rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b">
          <tr>
            <th className="text-left p-3 font-medium text-slate-600">Ad Soyad</th>
            <th className="text-left p-3 font-medium text-slate-600">Email</th>
            <th className="text-left p-3 font-medium text-slate-600">Telefon</th>
            <th className="text-left p-3 font-medium text-slate-600">Yetkiler</th>
            <th className="text-center p-3 font-medium text-slate-600">Durum</th>
            {showActions && <th className="text-right p-3 font-medium text-slate-600">Islem</th>}
          </tr>
        </thead>
        <tbody>
          {userList.map((u) => (
            <tr key={u.id} className="border-b hover:bg-slate-50">
              <td className="p-3 font-medium flex items-center gap-2">
                {u.role === 'admin' ? <Shield className="h-4 w-4 text-amber-500" /> : <User className="h-4 w-4 text-slate-400" />}
                {u.full_name}
              </td>
              <td className="p-3 text-slate-500">{u.email}</td>
              <td className="p-3 text-slate-500">{u.phone || '-'}</td>
              <td className="p-3">
                <div className="flex flex-wrap gap-1">
                  {u.role === 'admin' ? (
                    <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">Tam Yetki</span>
                  ) : (u.permissions || []).length > 0 ? (
                    (u.permissions || []).map(p => (
                      <span key={p} className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                        {PAGE_OPTIONS.find(o => o.key === p)?.label || p}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400">Yetki atanmamis</span>
                  )}
                </div>
              </td>
              <td className="p-3 text-center">
                {u.status === 'pending' ? (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">Onay Bekliyor</span>
                ) : u.status === 'rejected' ? (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Reddedildi</span>
                ) : u.is_active !== false ? (
                  <button onClick={() => showActions && toggleActive(u)}
                    className="px-2 py-0.5 rounded text-xs font-medium cursor-pointer bg-green-100 text-green-700">Aktif</button>
                ) : (
                  <button onClick={() => showActions && toggleActive(u)}
                    className="px-2 py-0.5 rounded text-xs font-medium cursor-pointer bg-red-100 text-red-700">Pasif</button>
                )}
              </td>
              {showActions && (
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    {u.status === 'pending' && (
                      <>
                        <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => approveUser(u)} data-testid={`approve-${u.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" />Onayla
                        </Button>
                        <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => rejectUser(u)}>
                          <X className="h-3.5 w-3.5 mr-1" />Reddet
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(u)} data-testid={`edit-user-${u.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    {u.id !== currentUser?.id && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteUser(u)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
          {userList.length === 0 && (
            <tr><td colSpan={6} className="p-6 text-center text-slate-400">Kullanıcı bulunamadi</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="hesap-users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Kullanıcı Yonetimi
          {pendingUsers.length > 0 && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {pendingUsers.length} Onay Bekliyor
            </span>
          )}
        </h1>
        <Button onClick={() => openDialog()} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-1" />Yeni Kullanıcı
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-200 rounded-lg p-1 w-fit">
        {[
          { key: 'pending', label: `Onay Bekleyenler (${pendingUsers.length})`, icon: Clock },
          { key: 'approved', label: `Aktif (${approvedUsers.length})`, icon: Check },
          { key: 'rejected', label: `Reddedilen (${rejectedUsers.length})`, icon: X },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition ${
              tab === t.key ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pending' && <UserTable userList={pendingUsers} />}
      {tab === 'approved' && <UserTable userList={approvedUsers} />}
      {tab === 'rejected' && <UserTable userList={rejectedUsers} />}

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Kullanıcıyi Duzenle' : 'Yeni Kullanıcı'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveUser} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Ad Soyad *</Label>
                <Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} data-testid="user-name-input" />
              </div>
              <div className="space-y-1">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{editing ? 'Yeni Şifre (bos = degismez)' : 'Şifre *'}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} />
              </div>
              <div className="space-y-1">
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Rol</Label>
              <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="user">Kullanıcı</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {form.role !== 'admin' && (
              <div className="space-y-2">
                <Label>Sayfa Yetkileri</Label>
                <p className="text-xs text-slate-500">Bu kullanicinin erisebilecegi sayfalari secin:</p>
                <div className="grid grid-cols-2 gap-2">
                  {PAGE_OPTIONS.map(opt => (
                    <label key={opt.key}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${
                        form.permissions.includes(opt.key)
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}>
                      <input type="checkbox" checked={form.permissions.includes(opt.key)}
                        onChange={() => togglePermission(opt.key)}
                        className="rounded border-slate-300 text-emerald-600" />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="save-user-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
